import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { hasOwnProperty, isObject } from "@next-core/utils/general";
import { strictCollectMemberUsage } from "@next-core/utils/storyboard";
import EventTarget from "@ungap/event-target";
import { pull } from "lodash";
import { eventCallbackFactory, listenerFactory } from "../bindListeners.js";
import { asyncCheckIf, checkIf } from "../compute/checkIf.js";
import { asyncComputeRealValue, computeRealValue } from "../compute/computeRealValue.js";
import { resolveData } from "./resolveData.js";
import { resolveDataStore } from "./resolveDataStore.js";
import { handleHttpError } from "../../handleHttpError.js";
import { computePropertyValue } from "../compute/computeRealProperties.js";
import { _internalApiGetRenderId } from "../Runtime.js";
import { callRealTimeDataInspectHooks, realTimeDataInspectRoot } from "./realTimeDataInspect.js";
import { isStrictMode } from "../../isStrictMode.js";
const supportContextResolveTriggerBrickLifeCycle = ["onBeforePageLoad", "onPageLoad", "onBeforePageLeave", "onPageLeave", "onAnchorLoad", "onAnchorUnload"];
export class DataStore {
  // 把 `rendererContext` 放在参数列表的最后，并作为可选，以减少测试文件的调整
  constructor(type, hostBrick, rendererContext, stateStoreId) {
    _defineProperty(this, "type", void 0);
    _defineProperty(this, "data", new Map());
    _defineProperty(this, "changeEventType", void 0);
    _defineProperty(this, "pendingStack", []);
    _defineProperty(this, "hostBrick", void 0);
    _defineProperty(this, "stateStoreId", void 0);
    _defineProperty(this, "batchUpdate", false);
    _defineProperty(this, "batchUpdateContextsNames", []);
    _defineProperty(this, "rendererContext", void 0);
    _defineProperty(this, "routeMap", new WeakMap());
    _defineProperty(this, "routeStackMap", new WeakMap());
    this.type = type;
    this.changeEventType = this.type === "FORM_STATE" ? "formstate.change" : this.type === "STATE" ? "state.change" : "context.change";
    this.hostBrick = hostBrick;
    this.rendererContext = rendererContext;
    this.stateStoreId = stateStoreId;
  }
  has(name) {
    return this.data.has(name);
  }
  getAllValues() {
    return Object.fromEntries([...this.data.entries()].map(_ref => {
      let [name, {
        value
      }] = _ref;
      return [name, value];
    }));
  }
  getValue(name) {
    var _this$data$get;
    return (_this$data$get = this.data.get(name)) === null || _this$data$get === void 0 ? void 0 : _this$data$get.value;
  }
  notifyRealTimeDataChange(name, value) {
    if (realTimeDataInspectRoot) {
      const {
        tplStateStoreId
      } = realTimeDataInspectRoot;
      if (tplStateStoreId ? this.type === "STATE" && this.stateStoreId === tplStateStoreId : this.type === "CTX") {
        callRealTimeDataInspectHooks({
          changeType: "update",
          tplStateStoreId,
          detail: {
            name,
            value
          }
        });
      }
    }
  }
  getAffectListByContext(name) {
    const affectNames = [name];
    this.data.forEach((value, key) => {
      if (value.deps) {
        const isInDeps = value.deps.some(item => affectNames.includes(item));
        isInDeps && affectNames.push(key) && affectNames.push(...this.getAffectListByContext(key));
      }
    });
    affectNames.shift();
    return [...new Set(affectNames)];
  }
  updateValues(values, method, argsFactory) {
    this.batchUpdate = true;
    this.batchUpdateContextsNames = values.map(item => item.name);
    if ([...new Set(this.batchUpdateContextsNames)].length !== this.batchUpdateContextsNames.length) {
      throw new Error(`Batch update not allow to update same item`);
    }
    const updateContexts = {};
    const affectContexts = {};
    const affectDepsContextNames = [];
    values.forEach(arg => {
      const {
        name,
        value
      } = argsFactory([arg]);
      const updateContextItem = this.data.get(name);
      affectDepsContextNames.push(...this.getAffectListByContext(name));
      updateContextItem && (updateContexts[name] = updateContextItem);
      this.updateValue(name, value, method);
    });
    affectDepsContextNames.filter(item => !updateContexts[item]).forEach(name => {
      const affectContextItem = this.data.get(name);
      affectContextItem && (affectContexts[name] = affectContextItem);
    });
    const triggerEvent = contexts => {
      for (const key in contexts) {
        var _context$eventTarget;
        const context = contexts[key];
        (_context$eventTarget = context.eventTarget) === null || _context$eventTarget === void 0 || _context$eventTarget.dispatchEvent(new CustomEvent(this.changeEventType, {
          detail: context.value
        }));
      }
    };
    triggerEvent(updateContexts);
    triggerEvent(affectContexts);
    this.batchUpdate = false;
    return;
  }
  updateValue(name, value, method, callback, callbackRuntimeContext) {
    const item = this.data.get(name);
    if (!item) {
      throw new Error(`${this.type} '${name}' is not defined`);
    }
    if (method === "refresh" || method === "load") {
      if (!item.useResolve) {
        throw new Error(`You can not ${method} "${this.type}.${name}" which is not using resolve`);
      }
      let promise;
      if (method === "load") {
        // Try to reuse previous request when calling `load`.
        if (item.loaded) {
          promise = Promise.resolve(item.value);
        } else if (item.loading) {
          promise = item.loading;
        }
      }
      const shouldDismiss = error => {
        var _this$rendererContext;
        // If render twice immediately, flow API contracts maybe cleared before
        // the second rendering, while the page load handlers of the first
        // rendering can't be cancelled, which throws `FlowApiNotFoundError`.
        // So we ignore error reporting for this case.
        let renderId;
        return (error === null || error === void 0 ? void 0 : error.name) === "FlowApiNotFoundError" && !!(renderId = (_this$rendererContext = this.rendererContext) === null || _this$rendererContext === void 0 ? void 0 : _this$rendererContext.renderId) && renderId !== _internalApiGetRenderId();
      };
      if (!promise) {
        promise = item.loading = item.load({
          cache: method === "load" ? "default" : "reload",
          ...value
        });
        // Do not use the chained promise, since the callbacks need the original promise.
        promise.then(val => {
          this.finishLoad(item, val);
        }, err => {
          // Let users override error handling.
          if (item.useResolve && !shouldDismiss(err) && !(callback !== null && callback !== void 0 && callback.error)) {
            handleHttpError(err);
          }
        });
      }
      if (callback) {
        const callbackFactory = eventCallbackFactory(callback, callbackRuntimeContext);
        promise.then(val => {
          callbackFactory("success")({
            value: val
          });
          callbackFactory("finally")();
        }, err => {
          if (!shouldDismiss(err) && callback.error) {
            callbackFactory("error")(err);
          }
          callbackFactory("finally")();
        });
      }
      return;
    }
    if (method === "replace") {
      item.value = value;
    } else if (method === "assign") {
      if (isObject(item.value)) {
        Object.assign(item.value, value);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`Non-object current value of "${this.type}.${name}" for "context.assign", try "context.replace" instead.`);
        item.value = value;
      }
    } else {
      // method === "set"
      // `context.set` and `state.set` is similar to React's setState,
      // which accepts either a value or an updater function.
      // And if the new value is the same as the current one, do nothing.
      let nextValue = value;
      if (typeof value === "function") {
        nextValue = value(item.value);
      }
      if (Object.is(item.value, nextValue)) {
        return;
      }
      item.value = nextValue;
    }
    if (this.batchUpdate) return;
    item.eventTarget.dispatchEvent(new CustomEvent(this.changeEventType, {
      detail: item.value
    }));
  }
  finishLoad(item, value) {
    if (!item.useResolve) {
      // This happens when a tracked conditional resolve switches from
      // resolve to fallback after an dep update triggered refresh but
      // before it's been resolved.
      return;
    }
    item.loaded = true;
    item.value = value;
    item.eventTarget.dispatchEvent(new CustomEvent(this.changeEventType, {
      detail: value
    }));
  }
  define(dataConfs, runtimeContext, asyncHostPropertyEntries, routePath) {
    if (Array.isArray(dataConfs) && dataConfs.length > 0) {
      const pending = resolveDataStore(dataConfs, dataConf => this.resolve(dataConf, runtimeContext, asyncHostPropertyEntries, routePath), this.type, isStrictMode(runtimeContext));
      if (Array.isArray(routePath)) {
        for (const {
          route
        } of routePath) {
          const stack = this.routeStackMap.get(route);
          if (stack) {
            stack.add(pending);
          } else {
            this.routeStackMap.set(route, new Set([pending]));
          }
        }
      }
      this.pendingStack.push(pending);
    }
  }
  onChange(dataName, listener) {
    var _this$data$get2;
    const eventTarget = (_this$data$get2 = this.data.get(dataName)) === null || _this$data$get2 === void 0 ? void 0 : _this$data$get2.eventTarget;
    eventTarget === null || eventTarget === void 0 || eventTarget.addEventListener(this.changeEventType, listener);
    return () => {
      eventTarget === null || eventTarget === void 0 || eventTarget.removeEventListener(this.changeEventType, listener);
    };
  }
  async waitFor(dataNames) {
    for (const {
      pendingContexts
    } of this.pendingStack) {
      await Promise.all([...dataNames].map(ctx => {
        const p = pendingContexts.get(ctx);
        return p;
      }));
    }
  }
  async waitForAll() {
    for (const {
      pendingResult
    } of this.pendingStack) {
      await pendingResult;
    }
  }

  /**
   * After mount, dispatch the change event when an async data is loaded.
   *
   * If param `route` is present, handle data defined in that route (or its descendants) only.
   */
  mountAsyncData(route) {
    this.data.forEach(item => {
      var _this$routeMap$get;
      if (item.async && (!route || (_this$routeMap$get = this.routeMap.get(route)) !== null && _this$routeMap$get !== void 0 && _this$routeMap$get.has(item.name))) {
        // istanbul ignore next
        if (item.asyncMounted) {
          // eslint-disable-next-line no-console
          console.error(`Async data "${item.name}" already mounted. This is a bug of Brick Next, please report it.`);
          return;
        }
        item.asyncMounted = true;
        // An async data always has `loading`
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        item.loading.then(value => {
          this.finishLoad(item, value);
        });
      }
    });
  }
  async resolve(dataConf, runtimeContext, asyncHostPropertyEntries, routePath) {
    if (!(await asyncCheckIf(dataConf, runtimeContext))) {
      return false;
    }
    let value;
    if (asyncHostPropertyEntries && (this.type === "STATE" ? dataConf.expose : this.type === "FORM_STATE")) {
      value = await computePropertyValue(asyncHostPropertyEntries, dataConf.name);
    }
    let load;
    let loading;
    let useResolve;
    let trackConditionalResolve;
    let resolvePolicy;
    if (value === undefined) {
      if (dataConf.resolve) {
        const hasFallbackValue = hasOwnProperty(dataConf, "value");
        // Track conditional resolve only if all matches:
        //   - Track enabled
        //   - Has fallback value
        //   - Referencing other data in `resolve.if`
        trackConditionalResolve = dataConf.track && hasFallbackValue && hasOwnProperty(dataConf.resolve, "if") && strictCollectMemberUsage(dataConf.resolve.if, this.type).size > 0;
        const resolveConf = {
          transform: "value",
          ...dataConf.resolve
        };
        useResolve = await asyncCheckIf(dataConf.resolve, runtimeContext);
        if (useResolve || trackConditionalResolve) {
          load = async options => {
            var _this$rendererContext2;
            return (await resolveData(resolveConf, runtimeContext, {
              ...options,
              renderId: (_this$rendererContext2 = this.rendererContext) === null || _this$rendererContext2 === void 0 ? void 0 : _this$rendererContext2.renderId
            })).value;
          };
        }
        if (useResolve) {
          // `async` take precedence over `lazy`
          resolvePolicy = dataConf.resolve.async ? "async" : dataConf.resolve.lazy ? "lazy" : "eager";
          if (resolvePolicy === "eager") {
            value = await load();
          } else if (resolvePolicy === "async") {
            loading = load();
          }
        } else if (!hasFallbackValue) {
          return false;
        }
      }
      if ((!useResolve || resolvePolicy !== "eager") && dataConf.value !== undefined) {
        // If the context has no resolve, just use its `value`.
        // Or if the resolve is ignored or lazy, use its `value` as a fallback.
        value = await asyncComputeRealValue(dataConf.value, runtimeContext);
      }
    }
    if (this.data.has(dataConf.name)) {
      throw new Error(`${this.type} '${dataConf.name}' has already been declared`);
    }
    const newData = {
      name: dataConf.name,
      value,
      // This is required for tracking context, even if no `onChange` is specified.
      eventTarget: new EventTarget(),
      useResolve,
      load,
      loaded: resolvePolicy === "eager",
      loading,
      async: resolvePolicy === "async",
      deps: []
    };
    if (resolvePolicy === "lazy") {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const {
        trigger
      } = dataConf.resolve;
      if (trigger && supportContextResolveTriggerBrickLifeCycle.includes(trigger)) {
        var _this$rendererContext3;
        (_this$rendererContext3 = this.rendererContext) === null || _this$rendererContext3 === void 0 || _this$rendererContext3.registerArbitraryLifeCycle(trigger, () => {
          this.updateValue(dataConf.name, undefined, "load");
        });
      }
    }
    if (dataConf.onChange) {
      newData.eventTarget.addEventListener(this.changeEventType, listenerFactory(dataConf.onChange, runtimeContext));
    }
    if (dataConf.track) {
      const deps = strictCollectMemberUsage(trackConditionalResolve ? [dataConf.resolve, dataConf.value] : load ? dataConf.resolve : dataConf.value, this.type);
      !load && (newData.deps = [...deps]);
      for (const dep of deps) {
        this.onChange(dep, this.batchAddListener(() => {
          newData.useResolve = trackConditionalResolve ? checkIf(dataConf.resolve, runtimeContext) : !!load;
          if (newData.useResolve) {
            this.updateValue(dataConf.name, {
              cache: "default"
            }, "refresh");
          } else {
            this.updateValue(dataConf.name, computeRealValue(dataConf.value, runtimeContext), "replace");
          }
        }, dataConf));
      }
    }
    newData.eventTarget.addEventListener(this.changeEventType, e => {
      this.notifyRealTimeDataChange(dataConf.name, e.detail);
    });
    this.data.set(dataConf.name, newData);
    if (Array.isArray(routePath)) {
      for (const {
        route
      } of routePath) {
        const names = this.routeMap.get(route);
        if (names) {
          names.add(dataConf.name);
        } else {
          this.routeMap.set(route, new Set([dataConf.name]));
        }
      }
    }
    return true;
  }

  /**
   * For sub-routes to be incrementally rendered,
   * dispose their data and pending tasks.
   */
  disposeDataInRoutes(routes) {
    for (const route of routes) {
      const names = this.routeMap.get(route);
      if (names !== undefined) {
        for (const name of names) {
          this.data.delete(name);
        }
        this.routeMap.delete(route);
      }
      const stack = this.routeStackMap.get(route);
      if (stack !== undefined) {
        pull(this.pendingStack, ...stack);
        this.routeStackMap.delete(route);
      }
    }
  }
  batchAddListener(listener, contextConf) {
    return event => {
      if (this.batchUpdate && this.batchUpdateContextsNames.includes(contextConf.name)) {
        return;
      }
      listener(event);
    };
  }
}
//# sourceMappingURL=DataStore.js.map